import AutoCompleteWithTag from '@/components/autoCompleteWithTag';
import BaseModal from '@/components/BaseModal';
import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import CustomCard from '@/components/customCard';
import Layout from '@/components/layout';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
import UpgradePlanBanner from '@/components/UpgradePlanBanner';
import { BREAKPOINT, Plan } from '@/constants/enum';
import { optionLanguage } from '@/constants/options';
import { findLanguage } from '@/helpers';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import bannerSlice, { bannerSelector } from '@/redux/slice/banner.slice';
import languageRuleSlice, { initLanguageRule } from '@/redux/slice/languageRule.slice';
import rulesSlice from '@/redux/slice/rules.slice';
import sessionSlice, { isShowBannerSelector } from '@/redux/slice/session.slice';
import settingsSlice, { dataSettingsBackupSelector, dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import { Badge, Banner, Button, ChoiceList, ChoiceListProps, Icon, InlineStack, Tag } from '@shopify/polaris';
import { DomainLandingPageIcon, LanguageTranslateIcon, PlusIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import CUModal from './components/CUModal';
import Table from './components/Table';
import { TranslationStyled } from './styled';

const methodsToDetectStoreLanguage: ChoiceListProps['choices'] = [
  {
    label: 'Shop locale',
    value: IParamsApi.DetectionMethod.ShopLocale,
    helpText: (
      <>
        <div>Get location and language preference settings in Shopline.</div>
        {/* <InlineError
          fieldID="shop_locale"
          message={'ETA checkout extension automatically uses shop locale, even if another detection method is chosen.'}
        /> */}
      </>
    ),
  },
  {
    label: 'IP address',
    value: IParamsApi.DetectionMethod.IPAddress,
    helpText: 'Detect location of visitor via IP and switch to primary language used in that location.',
  },
  {
    label: 'URL',
    value: IParamsApi.DetectionMethod.Url,
    helpText: 'Detect language based on URL path (e.g., https://domain.com/de for German).',
  },
  {
    label: 'Browser language',
    value: IParamsApi.DetectionMethod.BrowserLanguage,
    helpText: 'Detect browser language of visitor and switch accordingly.',
  },
];

const Translation = () => {
  useCustomUserIdClarity('Translation');
  const { show } = useToast();
  const location = useLocation();
  const dispatch = useDispatch();
  const { limitPlan, isFreePlan } = useUserPlan();
  const defaultLanguageRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });

  const isShowBannerLimit = useSelector(isShowBannerSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const dataSettingsBackup = useSelector(dataSettingsBackupSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const { data: languageRules } = apiCaller.useGetLanguageRulesQuery({ page: 1, perPage: -1 }, { skip: isSkip });
  const { isVisibleBannerInfoInTranslation } = useSelector(bannerSelector);

  const { data: allRules } = apiCaller.useGetRulesQuery(
    { page: 1, perPage: -1, sortBy: dataSettings.ruleSortBy },
    { skip: isSkip },
  );
  const { data: shopifyLanguage } = apiCaller.useGetShopifyLanguageQuery(undefined, {
    skip: isSkip || !dataSettings.enableReadLocales,
  });
  const [updateGeneralSetting, { isLoading: isLoadingUpdateSetting }] = apiCaller.useUpdateGeneralSettingMutation();

  const [isShowCUModal, setIsShowCUModal] = useState(false);
  const [isShowSelectLanguage, setIsShowSelectLanguage] = useState(false);
  const [isShowAddLanguage, setIsShowAddLanguage] = useState(false);
  const [isShowChangeMethod, setIsShowChangeMethod] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(optionLanguage[0].value);
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);

  const allLanguageRules = useMemo(() => {
    return languageRules?.data || [];
  }, [languageRules?.data]);

  const availableLanguages = useMemo(() => {
    const listLanguageUsed = allLanguageRules.map((rule) => rule.codeLanguage);
    return optionLanguage.filter((item) => !listLanguageUsed.includes(item.value) && item.value !== dataSettings.date_locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allLanguageRules)]);

  const isVisibleBannerWarningDefaultLanguage = useMemo(() => {
    return !!allLanguageRules.find((item) => item?.codeLanguage === dataSettings.date_locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allLanguageRules), dataSettings.date_locale]);

  useEffect(() => {
    if (!availableLanguages.length) return;
    setSelectedLanguage(availableLanguages[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(availableLanguages)]);

  const [selectedRuleId, setSelectedRuleId] = useState<number>();

  useEffect(() => {
    if (!location.state?.isSetDefaultLanguage || !defaultLanguageRef.current) {
      return;
    }

    const element = defaultLanguageRef.current;
    const scrollTimeout = setTimeout(() => {
      element.classList.add('highlight-border');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 1000);

    const highlightTimeout = setTimeout(() => {
      element.classList.remove('highlight-border');
    }, 4000);

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(highlightTimeout);
    };
  }, [location.state]);

  useEffect(() => {
    dispatch(rulesSlice.actions.saveAllRules(allRules?.data || []));
  }, [allRules?.data, dispatch]);

  const handleChangeDataSettings = (key: keyof typeof dataSettings) => (value: string) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const handleUpdateGeneralSetting = async () => {
    try {
      const res = await updateGeneralSetting(dataSettings).unwrap();
      setIsShowSelectLanguage(false);
      setIsShowChangeMethod(false);
      if (res.status === 'success') {
        show('Updated');
      }
    } catch {}
  };

  const handleAddLanguage = useCallback(
    (inputLanguage?: string) => {
      const currentLanguage = findLanguage(inputLanguage || selectedLanguage);
      if (!currentLanguage) return;

      const newLanguageRule = {
        ...initLanguageRule,
        nameLanguage: currentLanguage.label,
        codeLanguage: currentLanguage.value,
      };
      dispatch(languageRuleSlice.actions.handleSetSelectedRule(newLanguageRule));
      dispatch(languageRuleSlice.actions.handleSetBackupSelectedRule(newLanguageRule));

      setIsShowCUModal(true);
      setIsShowAddLanguage(false);
    },
    [dispatch, selectedLanguage],
  );

  const handleEdit = useCallback(
    (id: number) => {
      setSelectedRuleId(id);

      const currentLanguageRule = allLanguageRules.find((rule) => rule.id === id);
      if (!currentLanguageRule) return;

      dispatch(languageRuleSlice.actions.handleSetSelectedRule(currentLanguageRule));
      dispatch(languageRuleSlice.actions.handleSetBackupSelectedRule(currentLanguageRule));
      setIsShowCUModal(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(allLanguageRules), dispatch],
  );

  const handleCloseModalSelectLanguage = () => {
    setIsShowSelectLanguage(false);
    setOpenModalConfirmLeave(false);
    dispatch(settingsSlice.actions.handleDataSettings(dataSettingsBackup));
  };

  const handleCloseModalChangeMethodDetection = () => {
    setIsShowChangeMethod(false);
    setOpenModalConfirmLeave(false);
    dispatch(settingsSlice.actions.handleDataSettings(dataSettingsBackup));
  };

  const recommendedLanguage = useMemo(() => {
    if (!dataSettings.enableReadLocales) return null;
    const listShopifyLanguage = (shopifyLanguage?.data?.map((item) => findLanguage(item.locale)) || [])
      .filter(
        (item) => !allLanguageRules.some((rule) => rule.codeLanguage === item?.value) && item?.value !== dataSettings.date_locale,
      )
      .filter((item) => !!item);
    if (!listShopifyLanguage.length) return null;
    return (
      <div className="mt-16">
        <div className="mb-8">
          <BoldText>Recommended based on languages in your store settings.</BoldText>
        </div>
        <InlineStack gap={'200'}>
          {listShopifyLanguage.map((item) => (
            <Tag
              key={item.value}
              onClick={() => {
                setSelectedLanguage(item.value);
                handleAddLanguage(item.value);
              }}
            >
              <div className="d-flex">
                {item.label}
                <span style={{ marginRight: '-4px' }}>
                  <Icon source={PlusIcon} tone="subdued" />
                </span>
              </div>
            </Tag>
          ))}
        </InlineStack>
      </div>
    );
  }, [allLanguageRules, dataSettings.date_locale, dataSettings.enableReadLocales, handleAddLanguage, shopifyLanguage?.data]);

  const isDefaultLanguageChanged = useMemo(() => {
    return dataSettings?.date_locale !== dataSettingsBackup?.date_locale;
  }, [dataSettings?.date_locale, dataSettingsBackup?.date_locale]);

  const isDetectionMethodChanged = useMemo(() => {
    return dataSettings?.detectionMethod !== dataSettingsBackup?.detectionMethod;
  }, [dataSettings?.detectionMethod, dataSettingsBackup?.detectionMethod]);

  const handleClickCloseModal = () => {
    if (isDefaultLanguageChanged || isDetectionMethodChanged) setOpenModalConfirmLeave(true);
    else if (isShowSelectLanguage) handleCloseModalSelectLanguage();
    else if (isShowChangeMethod) handleCloseModalChangeMethodDetection();
  };

  return (
    <Layout title="Translation" subtitle="Localize customer facing copy of estimate rules, timeline graphic." fullWidth={false}>
      <TranslationStyled className="wrap-content d-flex flex-column gap-12">
        <div ref={defaultLanguageRef}>
          <CustomCard
            isResponsiveAction
            title={
              <div className="d-flex gap-4 align-center">
                <Icon source={LanguageTranslateIcon} />
                <span>{`Default language: ${findLanguage(dataSettings.date_locale)?.label || ''}`}</span>
              </div>
            }
            titleContentRight={
              <div className={isMobile ? 'mt-8' : ''}>
                <Button
                  onClick={() => {
                    setIsShowSelectLanguage(true);
                  }}
                >
                  Change default
                </Button>
              </div>
            }
          >
            <p>
              All estimate rules are recognized in this language by default, regardless of the input text. Rules must be created
              or edited in the default language.
            </p>
          </CustomCard>
        </div>

        <UpgradePlanBanner
          content="Localize estimate message, content for every operated market to build trust and confidence at global level"
          title="Translation is available for Standard plan and above"
          isVisible={dataSettings.plan === Plan.FREE && isShowBannerLimit.isVisibleBannerLimitInTranslation}
          onDismiss={() => {
            dispatch(sessionSlice.actions.handleBanner({ key: 'isVisibleBannerLimitInTranslation', value: false }));
          }}
        />

        <UpgradePlanBanner
          content={
            <b>
              <p>
                You have <b>{`${allLanguageRules.length}/${limitPlan?.maxLanguage}`}</b> language versions.
              </p>
              <p>Upgrade to support unlimited languages and localize content for every market to build trust at global level</p>
            </b>
          }
          title="Standard Plan Limit"
          isVisible={dataSettings.plan === Plan.STANDARD && isShowBannerLimit.isVisibleBannerLimitInTranslation}
          onDismiss={() => {
            dispatch(sessionSlice.actions.handleBanner({ key: 'isVisibleBannerLimitInTranslation', value: false }));
          }}
        />

        <CustomCard
          isResponsiveAction
          title={
            <div className="d-flex gap-4 align-center">
              <Icon source={DomainLandingPageIcon} />
              <span>Manage language version</span>
            </div>
          }
          titleContentRight={
            availableLanguages.length > 0 ? (
              <div className={isMobile ? 'mt-8' : ''}>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsShowAddLanguage(true);
                  }}
                  disabled={limitPlan && allLanguageRules.length >= (limitPlan?.maxLanguage || 0)}
                >
                  Add language
                </Button>
              </div>
            ) : undefined
          }
        >
          {isVisibleBannerInfoInTranslation && (
            <div className="my-16">
              <Banner
                tone="info"
                onDismiss={() =>
                  dispatch(bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleBannerInfoInTranslation', value: false }))
                }
              >
                Edit content for each added language and set it to active to display it in the store. If not set to active, the
                default language will be used.
              </Banner>
            </div>
          )}
          <div className="mt-8">
            <Table onEmptyAction={() => setIsShowAddLanguage(true)} onEdit={handleEdit} />
          </div>
        </CustomCard>

        <CustomCard
          isResponsiveAction
          title={
            <div className="d-flex gap-4 align-center">
              <Icon source={DomainLandingPageIcon} />
              <span>{`Language detection method: ${
                methodsToDetectStoreLanguage.find((item) => item.value === dataSettings.detectionMethod)?.label || ''
              }`}</span>
            </div>
          }
          titleContentRight={
            <div className={isMobile ? 'mt-8' : ''}>
              <Button
                onClick={() => {
                  setIsShowChangeMethod(true);
                }}
                disabled={isFreePlan}
              >
                Change method
              </Button>
            </div>
          }
        >
          <p>Detects the store’s language and automatically switches the ETA banner to the correct language version.</p>
        </CustomCard>
      </TranslationStyled>

      <BaseModal
        open={isShowAddLanguage}
        title="Add language"
        onClose={() => setIsShowAddLanguage(false)}
        primaryAction={{
          content: 'Add',
          onAction: () => handleAddLanguage(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setIsShowAddLanguage(false);
            },
          },
        ]}
      >
        <AutoCompleteWithTag
          options={availableLanguages}
          tag={[
            availableLanguages.find((item) => item.value === selectedLanguage) || {
              label: '',
              value: '',
            },
          ]}
          label={
            (
              <div className="d-flex gap-8 align-center">
                <BoldSmallText>Select a language: </BoldSmallText>
                <Badge tone="info">{availableLanguages.find((item) => item.value === selectedLanguage)?.label || ''}</Badge>
              </div>
            ) as any
          }
          selectedOptions={[selectedLanguage]}
          setSelectedOptions={(value) => {
            setSelectedLanguage(value[0].value);
          }}
          hasClearButton
        />
        {recommendedLanguage}
      </BaseModal>

      <BaseModal
        open={!openModalConfirmLeave && isShowSelectLanguage}
        title="Select default language"
        onClose={handleClickCloseModal}
        primaryAction={{ content: 'Save', onAction: handleUpdateGeneralSetting, loading: isLoadingUpdateSetting }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClickCloseModal,
            loading: isLoadingUpdateSetting,
          },
        ]}
      >
        <AutoCompleteWithTag
          options={optionLanguage}
          tag={[
            findLanguage(dataSettings.date_locale) || {
              label: '',
              value: '',
            },
          ]}
          label={
            (
              <div className="d-flex gap-8 align-center">
                <BoldSmallText>Select a language: </BoldSmallText>
                <Badge tone="info">{findLanguage(dataSettings.date_locale)?.label || ''}</Badge>
              </div>
            ) as any
          }
          selectedOptions={[dataSettings.date_locale]}
          setSelectedOptions={(value) => {
            handleChangeDataSettings('date_locale')(value[0].value);
          }}
          hasClearButton
        />
        {isVisibleBannerWarningDefaultLanguage && (
          <div className="mt-16">
            <Banner tone="critical">
              You already have <b>{findLanguage(dataSettings.date_locale)?.label || ''}</b> version. Saving default language as{' '}
              <b>{findLanguage(dataSettings.date_locale)?.label || ''}</b> will immediately make current version <b>Inactive</b>.
            </Banner>
          </div>
        )}
      </BaseModal>

      <BaseModal
        open={!openModalConfirmLeave && isShowChangeMethod}
        title="Language detection method"
        onClose={handleClickCloseModal}
        primaryAction={{ content: 'Save', onAction: handleUpdateGeneralSetting, loading: isLoadingUpdateSetting }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClickCloseModal,
            loading: isLoadingUpdateSetting,
          },
        ]}
      >
        <div className="d-flex flex-column gap-8">
          <p>Detects the store’s language and automatically switches the ETA banner to the correct language version.</p>
          <ChoiceList
            title=""
            titleHidden
            choices={methodsToDetectStoreLanguage}
            selected={[dataSettings.detectionMethod]}
            onChange={(value) => {
              handleChangeDataSettings('detectionMethod')(value[0]);
            }}
          />
        </div>
      </BaseModal>
      <CUModal id={selectedRuleId} setId={setSelectedRuleId} isOpen={isShowCUModal} setIsOpen={setIsShowCUModal} />
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={
          isDefaultLanguageChanged
            ? handleCloseModalSelectLanguage
            : isDetectionMethodChanged
            ? handleCloseModalChangeMethodDetection
            : () => {}
        }
        confirmButtonText="Confirm"
      />
    </Layout>
  );
};

export default Translation;
